import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { object, string as yupString } from "yup";
import { ErrorAlert } from "@/components/application/ErrorAlert";
import { Button } from "@/components/base/buttons/button";
import { Form } from "@/components/base/form/form";
import { Input, TextField } from "@/components/base/input/input";
import { Label } from "@/components/base/input/label";
import { Image } from "@/components/ui/image";
import { ShowPasswordButton } from "@/components/ui/showPasswordButton";
import { type UserRole, useAuth } from "@/context/AuthContext";
import { loginRequest } from "@/services/auth";
import { getUserCurrent } from "@/services/getUserCurrent";
import { setStyleByBreakpoint } from "@/styles/setStyleByBreakpoint";
import { useThemeToken } from "@/styles/tokens/useThemeTokens";
import { getDefaultAuthenticatedPath } from "@/utils/permissions";
import { sanitizeInput } from "@/utils/sanitizeInput";
import { loginStyles } from "./login.styles";

const uiText = {
	title: "Conectar",
	subtitle:
		"Boas vindas ao Fleeter!\nPara entrar na sua conta, insira seus dados abaixo.",
	emailLabel: "E-mail",
	emailPlaceholder: "Insira seu e-mail",
	emailError: "E-mail inválido",
	emailRequired: "E-mail é obrigatório",
	passwordLabel: "Senha",
	passwordPlaceholder: "Insira sua senha",
	passwordRequired: "Senha é obrigatória",
	submitButton: "Entrar",
	forgotPassword: "Esqueci minha senha",
	rememberConnection: "Me mantenha conectado",
};

type LoginForm = {
	email: string;
	password: string;
};

const schema = object().shape({
	email: yupString()
		.email(uiText.emailError)
		.transform((value: unknown) => sanitizeInput(String(value)))
		.required(uiText.emailRequired),
	password: yupString()
		.required(uiText.passwordRequired)
		.transform((value: unknown) => sanitizeInput(String(value))),
});

const Login = () => {
	const auth = useAuth();
	const navigate = useNavigate();
	const { styleInLine, classes } = loginStyles();
	const { getColors } = useThemeToken();

	const [apiError, setApiError] = useState<string | null>(null);
	const [showPassword, setShowPassword] = useState(false);

	const {
		control,
		handleSubmit,
		clearErrors,
		watch,
		formState: { errors },
	} = useForm<LoginForm>({
		resolver: yupResolver(schema),
		mode: "onBlur",
	});

	const onSubmit = async (data: LoginForm) => {
		setApiError(null);

		try {
			const response = await loginRequest(data);

			// Salva o token primeiro
			auth.login(response.accessToken, null, response.expiresAtUtc);

			// Tenta buscar os dados do usuário atual
			let authenticatedRoles: UserRole[] = [];
			try {
				const currentUser = await getUserCurrent();

				const normalizedUser = {
					...currentUser,
					role: currentUser.role.map(
						(r: string) => r.toLowerCase() as UserRole
					),
				};

				authenticatedRoles = normalizedUser.role;
				auth.login(response.accessToken, normalizedUser, response.expiresAtUtc);
			} catch (err: unknown) {
				// Se falhar ao buscar usuário, apenas loga o erro mas continua
				// O token já foi salvo, então o usuário pode continuar
				const error = err as { response?: { status?: number } };

				// Se for um erro 401/403, pode ser que o token ainda não esteja válido
				// Nesse caso, limpa o token e mostra erro
				if (
					error?.response?.status === 401 ||
					error?.response?.status === 403
				) {
					auth.logout();
					setApiError("Erro ao autenticar. Tente novamente.");
					return;
				}
			}

			const searchParams = new URLSearchParams(window.location.search);
			const redirectTo = searchParams.get("redirect");
			navigate(redirectTo || getDefaultAuthenticatedPath(authenticatedRoles));
		} catch (err: unknown) {
			const error = err as {
				response?: {
					data?: {
						error?: {
							type?: string;
							detail?: string;
						};
					};
				};
			};
			const apiData = error?.response?.data;

			if (
				apiData?.error?.type === "InvalidCredentialsException" ||
				apiData?.error?.type === "ValidationField"
			) {
				setApiError(apiData.error.detail || "Usuário ou senha inválidos.");
			} else {
				setApiError("Erro inesperado. Tente novamente mais tarde.");
			}
		}
	};

	useEffect(() => {
		if (!auth.loading && auth.user) {
			navigate(getDefaultAuthenticatedPath(auth.user.role), { replace: true });
		}
	}, [auth.user, auth.loading, navigate]);

	useEffect(() => {
		const subscription = watch((_value, { name }) => {
			if (apiError) {
				setApiError(null);
			}
			if (name && errors[name]) {
				clearErrors(name);
			}
		});

		return () => subscription.unsubscribe();
	}, [watch, errors, clearErrors, apiError]);

	return (
		<main
			className="main"
			data-testid="main_loginPage_container"
			style={{ display: "flex", height: "100vh" }}
		>
			<section
				data-testid="section_loginForm_container"
				style={styleInLine.loginFormContainer}
			>
				<div
					data-testid="div_loginForm_content"
					style={{ ...styleInLine.loginFormContent }}
				>
					<Image
						alt="Fleeter_Logo"
						data-testid="img_loginPage_logo"
						height={24}
						src="/Logo.svg"
						width={118}
					/>
					<div className={classes.login_section}>
						<div
							className={classes.login_text_block}
							data-testid="div_loginForm_header"
						>
							<h1
								className={classes.login_text_title}
								data-testid="div_loginForm_text_title"
							>
								{uiText.title}
							</h1>
							<p
								className={classes.login_text_subtitle}
								data-testid="div_loginForm_text_subtitle"
							>
								{uiText.subtitle}
							</p>
						</div>
						<Form
							data-testid="div_loginForm"
							onSubmit={handleSubmit(onSubmit)}
							style={styleInLine.loginForm}
						>
							<TextField
								aria-label="email"
								data-testid="div_loginForm_email_textfield"
								isRequired
								name="email"
								style={styleInLine.loginTextField}
								type="email"
							>
								<Label
									className="login_form_label"
									data-testid="label_loginForm_email"
									htmlFor="email"
								>
									{uiText.emailLabel}
								</Label>
								<Controller
									control={control}
									name="email"
									render={({ field }) => (
										<>
											<div
												style={{
													position: "relative",
													width: "100%",
													display: "flex",
													alignItems: "center",
												}}
											>
												<Input
													aria-invalid={!!errors.email}
													data-testid="div_loginForm_email_input"
													isInvalid={!!errors.email}
													maxLength={255}
													name={field.name}
													onBlur={field.onBlur}
													onChange={field.onChange}
													placeholder={uiText.emailPlaceholder}
													ref={field.ref}
													type="email"
													value={field.value ?? ""}
												/>
												{errors.email ? (
													<ErrorAlert color={getColors.fg_error_secondary} />
												) : null}
											</div>
											{errors.email ? (
												<span style={styleInLine.loginErrorMessage}>
													{errors.email.message}
												</span>
											) : null}
										</>
									)}
								/>
							</TextField>
							<TextField
								aria-label="password"
								data-testid="div_loginForm_password_textfield"
								isRequired
								name="password"
								style={styleInLine.loginTextField}
								type="password"
							>
								<Label
									className="login_form_label"
									data-testid="label_loginForm_password"
									htmlFor="password"
								>
									{uiText.passwordLabel}
								</Label>
								<Controller
									control={control}
									name="password"
									render={({ field }) => (
										<>
											<div
												style={{
													position: "relative",
													width: "100%",
													display: "flex",
													alignItems: "center",
												}}
											>
												<Input
													data-testid="div_loginForm_password_input"
													isInvalid={!!errors.password}
													maxLength={255}
													name={field.name}
													onBlur={field.onBlur}
													onChange={field.onChange}
													placeholder={uiText.passwordPlaceholder}
													ref={field.ref}
													type={showPassword ? "text" : "password"}
													value={field.value ?? ""}
												/>
												{field.value ? (
													<ShowPasswordButton
														setShowPassword={setShowPassword}
														showPassword={showPassword}
													/>
												) : null}
												{errors.password ? (
													<ErrorAlert color={getColors.fg_error_secondary} />
												) : null}
											</div>
											{errors.password ? (
												<span style={styleInLine.loginErrorMessage}>
													{errors.password.message}
												</span>
											) : null}
										</>
									)}
								/>
							</TextField>
							{apiError ? (
								<div
									className="form-error"
									data-testid="div_loginForm_api_error"
									style={styleInLine.loginErrorMessage}
								>
									{apiError}
								</div>
							) : null}
							<Button
								data-testid="button_loginForm_submit"
								style={styleInLine.loginBtnSubmit}
								type="submit"
							>
								{uiText.submitButton}
							</Button>
						</Form>
					</div>
				</div>
				<span style={styleInLine.loginBrand}>© Fleeter</span>
			</section>

			<section
				className={"login_container_image"}
				data-testid="section_dashboad_image_container"
			>
				<picture style={setStyleByBreakpoint(styleInLine.loginImage)}>
					<img
						alt="Fleeter_Logo"
						data-testid="img_dashboad_example"
						height={800}
						src="/dashboard_example.png"
						style={{ borderRadius: "inherit", width: "100%", height: "100%" }}
						width={1200}
					/>
				</picture>
			</section>
		</main>
	);
};

export default Login;
